import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getOrganisations, updateOrganisation } from '../api';
import Breadcrumb from '../components/Breadcrumb';

function EditForm({ org, onSave, onCancel }) {
  const [name, setName] = useState(org.name);
  const [slug, setSlug] = useState(org.slug);
  const [address, setAddress] = useState(org.address || '');
  const [telephone, setTelephone] = useState(org.telephone || '');
  const [website, setWebsite] = useState(org.website || '');
  const [contactEmail, setContactEmail] = useState(org.contactEmail || '');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const updated = await updateOrganisation(org.id, { name, slug, address, telephone, website, contactEmail, isActive: org.isActive });
      onSave(updated);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {error && <div className="alert alert-error">{error}</div>}
      <div className="form-row">
        <div className="form-group">
          <label>Name</label>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div className="form-group">
          <label>Slug</label>
          <input type="text" value={slug} onChange={(e) => setSlug(e.target.value)} required />
        </div>
      </div>
      <div className="form-group">
        <label>Address</label>
        <textarea rows={3} value={address} onChange={(e) => setAddress(e.target.value)} placeholder="e.g. Company Name&#10;Street&#10;City Postcode" />
      </div>
      <div className="form-row">
        <div className="form-group">
          <label>Telephone</label>
          <input type="text" value={telephone} onChange={(e) => setTelephone(e.target.value)} placeholder="e.g. 0161 877 0586" />
        </div>
        <div className="form-group">
          <label>Website</label>
          <input type="text" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="e.g. www.example.com" />
        </div>
        <div className="form-group">
          <label>Contact Email</label>
          <input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} placeholder="e.g. info@example.com" />
        </div>
      </div>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </button>
        <button type="button" className="btn btn-outline" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

export default function OrganisationSettingsPage() {
  const { isAdminForOrg } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    getOrganisations()
      .then((all) => {
        const adminOrgs = all.filter((o) => isAdminForOrg(o.id));
        setOrgs(adminOrgs);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [isAdminForOrg]);

  const handleSave = (updated) => {
    setOrgs((prev) => prev.map((o) => (o.id === updated.id ? updated : o)));
    setEditingId(null);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <div className="page-container">
      <div className="form-card">
        <Breadcrumb items={[
          { label: 'Dashboard', to: '/' },
          { label: 'Organisation Settings' },
        ]} />
        <h2>Organisation Settings</h2>

        {error && <div className="alert alert-error">{error}</div>}
        {saved && <div className="alert alert-success">Organisation updated successfully.</div>}
        {loading && <p>Loading...</p>}

        {!loading && orgs.map((org) => (
          <div key={org.id} style={{ marginTop: '1.5rem', padding: '1rem', border: '1px solid #e5e7eb', borderRadius: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <h3 style={{ margin: 0 }}>{org.name}</h3>
              {editingId !== org.id && (
                <button className="btn btn-outline" style={{ fontSize: '0.85rem' }} onClick={() => setEditingId(org.id)}>Edit</button>
              )}
            </div>
            {editingId === org.id ? (
              <EditForm org={org} onSave={handleSave} onCancel={() => setEditingId(null)} />
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '0.4rem 1rem', fontSize: '0.9rem', color: '#555' }}>
                <span style={{ fontWeight: 600 }}>Slug</span><span>{org.slug}</span>
                <span style={{ fontWeight: 600 }}>Address</span><span style={{ whiteSpace: 'pre-line' }}>{org.address || '-'}</span>
                <span style={{ fontWeight: 600 }}>Telephone</span><span>{org.telephone || '-'}</span>
                <span style={{ fontWeight: 600 }}>Website</span><span>{org.website || '-'}</span>
                <span style={{ fontWeight: 600 }}>Contact Email</span><span>{org.contactEmail || '-'}</span>
              </div>
            )}
          </div>
        ))}

        {!loading && orgs.length === 0 && !error && (
          <p style={{ color: '#888', marginTop: '1rem' }}>You are not an administrator for any organisations.</p>
        )}
      </div>
    </div>
  );
}
